import type { Locale } from '@/config/locales';

type LegalDocumentKey = 'privacyNotice' | 'termsAndConditions' | 'cookiePolicy';

export interface LegalLandingPageTranslation {
  backAriaLabel: string;
  pageTitle: string;
  companyName: string;
  responsibleEyebrow: string;
  responsibleTitle: string;
  legalNameLabel: string;
  rfcLabel: string;
  taxAddressLabel: string;
  privacyEmailLabel: string;
  publicDocumentsEyebrow: string;
  legalDocumentsTitle: string;
  documentUpdatedLabel: string;
  openPdf: string;
  arcoEyebrow: string;
  arcoTitle: string;
  arcoIntroBeforeEmail: string;
  arcoIntroAfterEmail: string;
  arcoRequirements: string[];
  responseBeforeInai: string;
  responseAfterInai: string;
  inaiLinkLabel: string;
  documents: Record<LegalDocumentKey, string>;
}

export const legalDocumentUpdatedAt: Record<Locale, string> = {
  es: '20 de abril de 2026',
  en: 'April 20, 2026',
  ko: '2026년 4월 20일',
  pt: '20 de abril de 2026',
  fr: '20 avril 2026',
  zh: '2026年4月20日',
  ja: '2026年4月20日',
  hi: '20 अप्रैल 2026',
  de: '20. April 2026',
  it: '20 aprile 2026',
  ru: '20 апреля 2026',
  ar: '20 أبريل 2026',
  vi: '20 tháng 4 năm 2026',
  th: '20 เมษายน 2026',
  nl: '20 april 2026',
};

export const legalLandingPageTranslations: Record<Locale, LegalLandingPageTranslation> = {
  es: {
    backAriaLabel: 'Volver',
    pageTitle: 'Información legal',
    companyName: 'OLULO, S.A. de C.V.',
    responsibleEyebrow: 'Responsable del tratamiento',
    responsibleTitle: 'OLULO, S.A. de C.V.',
    legalNameLabel: 'Razón social',
    rfcLabel: 'RFC',
    taxAddressLabel: 'Domicilio fiscal',
    privacyEmailLabel: 'Correo de privacidad',
    publicDocumentsEyebrow: 'Documentos públicos',
    legalDocumentsTitle: 'Documentos legales',
    documentUpdatedLabel: 'Última actualización',
    openPdf: 'Abrir PDF',
    arcoEyebrow: 'Derechos ARCO',
    arcoTitle: 'Canal de ejercicio de derechos',
    arcoIntroBeforeEmail:
      'Para ejercer derechos de Acceso, Rectificación, Cancelación u Oposición, revocar el consentimiento o limitar el uso o divulgación de datos personales, envíe una solicitud a',
    arcoIntroAfterEmail: '.',
    arcoRequirements: [
      'Incluya nombre completo y un medio de contacto.',
      'Adjunte copia de identificación oficial vigente.',
      'Describa claramente el derecho que desea ejercer.',
      'Agregue documentos que faciliten la localización de sus datos, si aplica.',
    ],
    responseBeforeInai:
      'OLULO responderá en un plazo máximo de 20 días hábiles conforme al artículo 32 de la LFPDPPP. En caso de inconformidad, puede acudir al INAI en',
    responseAfterInai: '.',
    inaiLinkLabel: 'www.inai.org.mx',
    documents: {
      privacyNotice: 'Aviso de Privacidad',
      termsAndConditions: 'Términos y Condiciones',
      cookiePolicy: 'Política de Cookies',
    },
  },
  en: {
    backAriaLabel: 'Back',
    pageTitle: 'Legal information',
    companyName: 'OLULO, S.A. de C.V.',
    responsibleEyebrow: 'Data controller',
    responsibleTitle: 'OLULO, S.A. de C.V.',
    legalNameLabel: 'Legal name',
    rfcLabel: 'RFC',
    taxAddressLabel: 'Tax address',
    privacyEmailLabel: 'Privacy email',
    publicDocumentsEyebrow: 'Public documents',
    legalDocumentsTitle: 'Legal documents',
    documentUpdatedLabel: 'Last updated',
    openPdf: 'Open PDF',
    arcoEyebrow: 'ARCO rights',
    arcoTitle: 'Rights request channel',
    arcoIntroBeforeEmail:
      'To exercise Access, Rectification, Cancellation or Opposition rights, revoke consent, or limit the use or disclosure of personal data, send a request to',
    arcoIntroAfterEmail: '.',
    arcoRequirements: [
      'Include your full name and a contact method.',
      'Attach a copy of a valid official ID.',
      'Clearly describe the right you want to exercise.',
      'Add documents that help locate your data, if applicable.',
    ],
    responseBeforeInai:
      'OLULO will respond within a maximum of 20 business days under Article 32 of the LFPDPPP. If you disagree with the response, you may contact INAI at',
    responseAfterInai: '.',
    inaiLinkLabel: 'www.inai.org.mx',
    documents: {
      privacyNotice: 'Privacy Notice',
      termsAndConditions: 'Terms and Conditions',
      cookiePolicy: 'Cookie Policy',
    },
  },
  ko: {
    backAriaLabel: '뒤로가기',
    pageTitle: '법률 정보',
    companyName: 'OLULO, S.A. de C.V.',
    responsibleEyebrow: '개인정보 처리 책임자',
    responsibleTitle: 'OLULO, S.A. de C.V.',
    legalNameLabel: '법인명',
    rfcLabel: 'RFC',
    taxAddressLabel: '세무 주소',
    privacyEmailLabel: '개인정보 문의 이메일',
    publicDocumentsEyebrow: '공개 문서',
    legalDocumentsTitle: '법률 문서',
    documentUpdatedLabel: '최종 업데이트',
    openPdf: 'PDF 열기',
    arcoEyebrow: 'ARCO 권리',
    arcoTitle: '권리 행사 채널',
    arcoIntroBeforeEmail:
      '접근, 정정, 취소 또는 반대 권리 행사, 동의 철회, 개인정보 사용 또는 공개 제한을 요청하려면 다음 주소로 신청서를 보내십시오:',
    arcoIntroAfterEmail: '.',
    arcoRequirements: [
      '성명과 연락 가능한 수단을 포함하십시오.',
      '유효한 공식 신분증 사본을 첨부하십시오.',
      '행사하려는 권리를 명확하게 설명하십시오.',
      '해당되는 경우 데이터 확인에 도움이 되는 문서를 추가하십시오.',
    ],
    responseBeforeInai:
      'OLULO는 LFPDPPP 제32조에 따라 최대 20영업일 이내에 응답합니다. 응답에 이의가 있는 경우 INAI에 문의할 수 있습니다:',
    responseAfterInai: '.',
    inaiLinkLabel: 'www.inai.org.mx',
    documents: {
      privacyNotice: '개인정보 처리방침',
      termsAndConditions: '이용약관',
      cookiePolicy: '쿠키 정책',
    },
  },
  pt: {
    backAriaLabel: 'Voltar',
    pageTitle: 'Informações legais',
    companyName: 'OLULO, S.A. de C.V.',
    responsibleEyebrow: 'Responsável pelo tratamento',
    responsibleTitle: 'OLULO, S.A. de C.V.',
    legalNameLabel: 'Razão social',
    rfcLabel: 'RFC',
    taxAddressLabel: 'Endereço fiscal',
    privacyEmailLabel: 'E-mail de privacidade',
    publicDocumentsEyebrow: 'Documentos públicos',
    legalDocumentsTitle: 'Documentos legais',
    documentUpdatedLabel: 'Última atualização',
    openPdf: 'Abrir PDF',
    arcoEyebrow: 'Direitos ARCO',
    arcoTitle: 'Canal para exercício de direitos',
    arcoIntroBeforeEmail:
      'Para exercer direitos de Acesso, Retificação, Cancelamento ou Oposição, revogar o consentimento ou limitar o uso ou divulgação de dados pessoais, envie uma solicitação para',
    arcoIntroAfterEmail: '.',
    arcoRequirements: [
      'Inclua nome completo e um meio de contato.',
      'Anexe cópia de identificação oficial válida.',
      'Descreva claramente o direito que deseja exercer.',
      'Inclua documentos que facilitem a localização dos seus dados, se aplicável.',
    ],
    responseBeforeInai:
      'A OLULO responderá em até 20 dias úteis conforme o artigo 32 da LFPDPPP. Em caso de inconformidade, você pode procurar o INAI em',
    responseAfterInai: '.',
    inaiLinkLabel: 'www.inai.org.mx',
    documents: {
      privacyNotice: 'Aviso de Privacidade',
      termsAndConditions: 'Termos e Condições',
      cookiePolicy: 'Política de Cookies',
    },
  },
  fr: {
    backAriaLabel: 'Retour',
    pageTitle: 'Informations légales',
    companyName: 'OLULO, S.A. de C.V.',
    responsibleEyebrow: 'Responsable du traitement',
    responsibleTitle: 'OLULO, S.A. de C.V.',
    legalNameLabel: 'Raison sociale',
    rfcLabel: 'RFC',
    taxAddressLabel: 'Adresse fiscale',
    privacyEmailLabel: 'E-mail confidentialité',
    publicDocumentsEyebrow: 'Documents publics',
    legalDocumentsTitle: 'Documents juridiques',
    documentUpdatedLabel: 'Dernière mise à jour',
    openPdf: 'Ouvrir le PDF',
    arcoEyebrow: 'Droits ARCO',
    arcoTitle: 'Canal d’exercice des droits',
    arcoIntroBeforeEmail:
      'Pour exercer vos droits d’accès, de rectification, d’annulation ou d’opposition, révoquer votre consentement ou limiter l’utilisation ou la divulgation de données personnelles, envoyez une demande à',
    arcoIntroAfterEmail: '.',
    arcoRequirements: [
      'Indiquez votre nom complet et un moyen de contact.',
      'Joignez une copie d’une pièce d’identité officielle valide.',
      'Décrivez clairement le droit que vous souhaitez exercer.',
      'Ajoutez les documents facilitant la localisation de vos données, le cas échéant.',
    ],
    responseBeforeInai:
      'OLULO répondra dans un délai maximal de 20 jours ouvrables conformément à l’article 32 de la LFPDPPP. En cas de désaccord, vous pouvez contacter l’INAI sur',
    responseAfterInai: '.',
    inaiLinkLabel: 'www.inai.org.mx',
    documents: {
      privacyNotice: 'Avis de confidentialité',
      termsAndConditions: 'Conditions générales',
      cookiePolicy: 'Politique relative aux cookies',
    },
  },
  zh: {
    backAriaLabel: '返回',
    pageTitle: '法律信息',
    companyName: 'OLULO, S.A. de C.V.',
    responsibleEyebrow: '数据处理负责人',
    responsibleTitle: 'OLULO, S.A. de C.V.',
    legalNameLabel: '法定名称',
    rfcLabel: 'RFC',
    taxAddressLabel: '税务地址',
    privacyEmailLabel: '隐私联系邮箱',
    publicDocumentsEyebrow: '公开文件',
    legalDocumentsTitle: '法律文件',
    documentUpdatedLabel: '最后更新',
    openPdf: '打开 PDF',
    arcoEyebrow: 'ARCO 权利',
    arcoTitle: '权利行使渠道',
    arcoIntroBeforeEmail:
      '如需行使访问、更正、取消或反对权利，撤回同意，或限制个人数据的使用或披露，请将申请发送至',
    arcoIntroAfterEmail: '。',
    arcoRequirements: [
      '请包含您的全名和联系方式。',
      '请附上有效官方身份证件副本。',
      '请清楚说明您希望行使的权利。',
      '如适用，请补充有助于定位您数据的文件。',
    ],
    responseBeforeInai:
      'OLULO 将根据 LFPDPPP 第32条在最多 20 个工作日内回复。如有异议，您可以通过以下地址联系 INAI：',
    responseAfterInai: '。',
    inaiLinkLabel: 'www.inai.org.mx',
    documents: {
      privacyNotice: '隐私声明',
      termsAndConditions: '条款和条件',
      cookiePolicy: 'Cookie 政策',
    },
  },
  ja: {
    backAriaLabel: '戻る',
    pageTitle: '法的情報',
    companyName: 'OLULO, S.A. de C.V.',
    responsibleEyebrow: '個人データ管理者',
    responsibleTitle: 'OLULO, S.A. de C.V.',
    legalNameLabel: '法人名',
    rfcLabel: 'RFC',
    taxAddressLabel: '税務上の住所',
    privacyEmailLabel: 'プライバシー連絡先メール',
    publicDocumentsEyebrow: '公開文書',
    legalDocumentsTitle: '法的文書',
    documentUpdatedLabel: '最終更新日',
    openPdf: 'PDFを開く',
    arcoEyebrow: 'ARCO権利',
    arcoTitle: '権利行使の窓口',
    arcoIntroBeforeEmail:
      'アクセス、訂正、取消または異議申立ての権利行使、同意の撤回、個人データの利用または開示の制限を希望する場合は、次の宛先に申請を送信してください:',
    arcoIntroAfterEmail: '。',
    arcoRequirements: [
      '氏名と連絡手段を記載してください。',
      '有効な公的身分証明書のコピーを添付してください。',
      '行使したい権利を明確に説明してください。',
      '該当する場合、データの特定に役立つ書類を追加してください。',
    ],
    responseBeforeInai:
      'OLULOはLFPDPPP第32条に従い、最大20営業日以内に回答します。回答に不服がある場合は、INAIに連絡できます:',
    responseAfterInai: '。',
    inaiLinkLabel: 'www.inai.org.mx',
    documents: {
      privacyNotice: 'プライバシー通知',
      termsAndConditions: '利用規約',
      cookiePolicy: 'Cookieポリシー',
    },
  },
  hi: {
    backAriaLabel: 'वापस',
    pageTitle: 'कानूनी जानकारी',
    companyName: 'OLULO, S.A. de C.V.',
    responsibleEyebrow: 'डेटा नियंत्रक',
    responsibleTitle: 'OLULO, S.A. de C.V.',
    legalNameLabel: 'कानूनी नाम',
    rfcLabel: 'RFC',
    taxAddressLabel: 'कर पता',
    privacyEmailLabel: 'गोपनीयता ईमेल',
    publicDocumentsEyebrow: 'सार्वजनिक दस्तावेज़',
    legalDocumentsTitle: 'कानूनी दस्तावेज़',
    documentUpdatedLabel: 'अंतिम अपडेट',
    openPdf: 'PDF खोलें',
    arcoEyebrow: 'ARCO अधिकार',
    arcoTitle: 'अधिकार अनुरोध चैनल',
    arcoIntroBeforeEmail:
      'एक्सेस, संशोधन, रद्दीकरण या आपत्ति अधिकारों का प्रयोग करने, सहमति वापस लेने, या व्यक्तिगत डेटा के उपयोग या प्रकटीकरण को सीमित करने के लिए अनुरोध भेजें',
    arcoIntroAfterEmail: 'पर।',
    arcoRequirements: [
      'अपना पूरा नाम और संपर्क माध्यम शामिल करें।',
      'वैध आधिकारिक पहचान पत्र की प्रति संलग्न करें।',
      'आप जिस अधिकार का प्रयोग करना चाहते हैं उसका स्पष्ट वर्णन करें।',
      'यदि लागू हो, तो अपने डेटा का पता लगाने में सहायक दस्तावेज़ जोड़ें।',
    ],
    responseBeforeInai:
      'OLULO LFPDPPP के अनुच्छेद 32 के अनुसार अधिकतम 20 कार्य दिवसों में उत्तर देगा। असहमति की स्थिति में, आप INAI से संपर्क कर सकते हैं:',
    responseAfterInai: '।',
    inaiLinkLabel: 'www.inai.org.mx',
    documents: {
      privacyNotice: 'गोपनीयता सूचना',
      termsAndConditions: 'नियम और शर्तें',
      cookiePolicy: 'कुकी नीति',
    },
  },
  de: {
    backAriaLabel: 'Zurück',
    pageTitle: 'Rechtliche Informationen',
    companyName: 'OLULO, S.A. de C.V.',
    responsibleEyebrow: 'Verantwortlicher für die Verarbeitung',
    responsibleTitle: 'OLULO, S.A. de C.V.',
    legalNameLabel: 'Rechtsname',
    rfcLabel: 'RFC',
    taxAddressLabel: 'Steueradresse',
    privacyEmailLabel: 'E-Mail für Datenschutz',
    publicDocumentsEyebrow: 'Öffentliche Dokumente',
    legalDocumentsTitle: 'Rechtliche Dokumente',
    documentUpdatedLabel: 'Zuletzt aktualisiert',
    openPdf: 'PDF öffnen',
    arcoEyebrow: 'ARCO-Rechte',
    arcoTitle: 'Kanal zur Ausübung von Rechten',
    arcoIntroBeforeEmail:
      'Um Auskunfts-, Berichtigungs-, Löschungs- oder Widerspruchsrechte auszuüben, eine Einwilligung zu widerrufen oder die Nutzung oder Offenlegung personenbezogener Daten einzuschränken, senden Sie eine Anfrage an',
    arcoIntroAfterEmail: '.',
    arcoRequirements: [
      'Geben Sie Ihren vollständigen Namen und eine Kontaktmöglichkeit an.',
      'Fügen Sie eine Kopie eines gültigen amtlichen Ausweises bei.',
      'Beschreiben Sie klar, welches Recht Sie ausüben möchten.',
      'Fügen Sie gegebenenfalls Dokumente hinzu, die das Auffinden Ihrer Daten erleichtern.',
    ],
    responseBeforeInai:
      'OLULO antwortet gemäß Artikel 32 der LFPDPPP innerhalb von höchstens 20 Werktagen. Bei Unzufriedenheit können Sie sich an INAI wenden:',
    responseAfterInai: '.',
    inaiLinkLabel: 'www.inai.org.mx',
    documents: {
      privacyNotice: 'Datenschutzhinweis',
      termsAndConditions: 'Allgemeine Geschäftsbedingungen',
      cookiePolicy: 'Cookie-Richtlinie',
    },
  },
  it: {
    backAriaLabel: 'Indietro',
    pageTitle: 'Informazioni legali',
    companyName: 'OLULO, S.A. de C.V.',
    responsibleEyebrow: 'Titolare del trattamento',
    responsibleTitle: 'OLULO, S.A. de C.V.',
    legalNameLabel: 'Ragione sociale',
    rfcLabel: 'RFC',
    taxAddressLabel: 'Domicilio fiscale',
    privacyEmailLabel: 'E-mail privacy',
    publicDocumentsEyebrow: 'Documenti pubblici',
    legalDocumentsTitle: 'Documenti legali',
    documentUpdatedLabel: 'Ultimo aggiornamento',
    openPdf: 'Apri PDF',
    arcoEyebrow: 'Diritti ARCO',
    arcoTitle: 'Canale per esercitare i diritti',
    arcoIntroBeforeEmail:
      'Per esercitare i diritti di accesso, rettifica, cancellazione o opposizione, revocare il consenso o limitare l’uso o la divulgazione dei dati personali, inviare una richiesta a',
    arcoIntroAfterEmail: '.',
    arcoRequirements: [
      'Includere nome completo e un mezzo di contatto.',
      'Allegare copia di un documento ufficiale valido.',
      'Descrivere chiaramente il diritto che si desidera esercitare.',
      'Aggiungere documenti che facilitino l’individuazione dei dati, se applicabile.',
    ],
    responseBeforeInai:
      'OLULO risponderà entro un massimo di 20 giorni lavorativi ai sensi dell’articolo 32 della LFPDPPP. In caso di disaccordo, è possibile rivolgersi all’INAI su',
    responseAfterInai: '.',
    inaiLinkLabel: 'www.inai.org.mx',
    documents: {
      privacyNotice: 'Informativa sulla privacy',
      termsAndConditions: 'Termini e condizioni',
      cookiePolicy: 'Informativa sui cookie',
    },
  },
  ru: {
    backAriaLabel: 'Назад',
    pageTitle: 'Правовая информация',
    companyName: 'OLULO, S.A. de C.V.',
    responsibleEyebrow: 'Ответственный за обработку данных',
    responsibleTitle: 'OLULO, S.A. de C.V.',
    legalNameLabel: 'Юридическое наименование',
    rfcLabel: 'RFC',
    taxAddressLabel: 'Налоговый адрес',
    privacyEmailLabel: 'Адрес по вопросам конфиденциальности',
    publicDocumentsEyebrow: 'Публичные документы',
    legalDocumentsTitle: 'Юридические документы',
    documentUpdatedLabel: 'Последнее обновление',
    openPdf: 'Открыть PDF',
    arcoEyebrow: 'Права ARCO',
    arcoTitle: 'Канал для реализации прав',
    arcoIntroBeforeEmail:
      'Чтобы реализовать права доступа, исправления, удаления или возражения, отозвать согласие либо ограничить использование или раскрытие персональных данных, отправьте запрос на',
    arcoIntroAfterEmail: '.',
    arcoRequirements: [
      'Укажите полное имя и способ связи.',
      'Приложите копию действующего официального удостоверения личности.',
      'Четко опишите право, которое хотите реализовать.',
      'При необходимости добавьте документы, помогающие найти ваши данные.',
    ],
    responseBeforeInai:
      'OLULO ответит в срок до 20 рабочих дней в соответствии со статьей 32 LFPDPPP. При несогласии с ответом вы можете обратиться в INAI по адресу',
    responseAfterInai: '.',
    inaiLinkLabel: 'www.inai.org.mx',
    documents: {
      privacyNotice: 'Уведомление о конфиденциальности',
      termsAndConditions: 'Условия и положения',
      cookiePolicy: 'Политика использования cookie',
    },
  },
  ar: {
    backAriaLabel: 'رجوع',
    pageTitle: 'المعلومات القانونية',
    companyName: 'OLULO, S.A. de C.V.',
    responsibleEyebrow: 'المسؤول عن معالجة البيانات',
    responsibleTitle: 'OLULO, S.A. de C.V.',
    legalNameLabel: 'الاسم القانوني',
    rfcLabel: 'RFC',
    taxAddressLabel: 'العنوان الضريبي',
    privacyEmailLabel: 'بريد الخصوصية',
    publicDocumentsEyebrow: 'المستندات العامة',
    legalDocumentsTitle: 'المستندات القانونية',
    documentUpdatedLabel: 'آخر تحديث',
    openPdf: 'فتح PDF',
    arcoEyebrow: 'حقوق ARCO',
    arcoTitle: 'قناة ممارسة الحقوق',
    arcoIntroBeforeEmail:
      'لممارسة حقوق الوصول أو التصحيح أو الإلغاء أو الاعتراض، أو سحب الموافقة، أو تقييد استخدام البيانات الشخصية أو الإفصاح عنها، أرسل طلبًا إلى',
    arcoIntroAfterEmail: '.',
    arcoRequirements: [
      'اذكر الاسم الكامل ووسيلة للتواصل.',
      'أرفق نسخة من هوية رسمية سارية.',
      'صف بوضوح الحق الذي تريد ممارسته.',
      'أضف المستندات التي تساعد في تحديد بياناتك، إن وجدت.',
    ],
    responseBeforeInai:
      'سترد OLULO خلال مدة أقصاها 20 يوم عمل وفقًا للمادة 32 من LFPDPPP. إذا لم تكن راضيًا، يمكنك التواصل مع INAI عبر',
    responseAfterInai: '.',
    inaiLinkLabel: 'www.inai.org.mx',
    documents: {
      privacyNotice: 'إشعار الخصوصية',
      termsAndConditions: 'الشروط والأحكام',
      cookiePolicy: 'سياسة ملفات تعريف الارتباط',
    },
  },
  vi: {
    backAriaLabel: 'Quay lại',
    pageTitle: 'Thông tin pháp lý',
    companyName: 'OLULO, S.A. de C.V.',
    responsibleEyebrow: 'Bên chịu trách nhiệm xử lý',
    responsibleTitle: 'OLULO, S.A. de C.V.',
    legalNameLabel: 'Tên pháp lý',
    rfcLabel: 'RFC',
    taxAddressLabel: 'Địa chỉ thuế',
    privacyEmailLabel: 'Email về quyền riêng tư',
    publicDocumentsEyebrow: 'Tài liệu công khai',
    legalDocumentsTitle: 'Tài liệu pháp lý',
    documentUpdatedLabel: 'Cập nhật lần cuối',
    openPdf: 'Mở PDF',
    arcoEyebrow: 'Quyền ARCO',
    arcoTitle: 'Kênh thực hiện quyền',
    arcoIntroBeforeEmail:
      'Để thực hiện quyền truy cập, chỉnh sửa, hủy bỏ hoặc phản đối, thu hồi sự đồng ý, hoặc giới hạn việc sử dụng hay tiết lộ dữ liệu cá nhân, hãy gửi yêu cầu đến',
    arcoIntroAfterEmail: '.',
    arcoRequirements: [
      'Bao gồm họ tên đầy đủ và phương thức liên hệ.',
      'Đính kèm bản sao giấy tờ tùy thân chính thức còn hiệu lực.',
      'Mô tả rõ quyền bạn muốn thực hiện.',
      'Bổ sung tài liệu giúp định vị dữ liệu của bạn, nếu có.',
    ],
    responseBeforeInai:
      'OLULO sẽ phản hồi trong tối đa 20 ngày làm việc theo Điều 32 của LFPDPPP. Nếu không đồng ý, bạn có thể liên hệ INAI tại',
    responseAfterInai: '.',
    inaiLinkLabel: 'www.inai.org.mx',
    documents: {
      privacyNotice: 'Thông báo về quyền riêng tư',
      termsAndConditions: 'Điều khoản và điều kiện',
      cookiePolicy: 'Chính sách cookie',
    },
  },
  th: {
    backAriaLabel: 'ย้อนกลับ',
    pageTitle: 'ข้อมูลทางกฎหมาย',
    companyName: 'OLULO, S.A. de C.V.',
    responsibleEyebrow: 'ผู้ควบคุมข้อมูล',
    responsibleTitle: 'OLULO, S.A. de C.V.',
    legalNameLabel: 'ชื่อนิติบุคคล',
    rfcLabel: 'RFC',
    taxAddressLabel: 'ที่อยู่ทางภาษี',
    privacyEmailLabel: 'อีเมลด้านความเป็นส่วนตัว',
    publicDocumentsEyebrow: 'เอกสารสาธารณะ',
    legalDocumentsTitle: 'เอกสารทางกฎหมาย',
    documentUpdatedLabel: 'อัปเดตล่าสุด',
    openPdf: 'เปิด PDF',
    arcoEyebrow: 'สิทธิ ARCO',
    arcoTitle: 'ช่องทางใช้สิทธิ',
    arcoIntroBeforeEmail:
      'หากต้องการใช้สิทธิในการเข้าถึง แก้ไข ยกเลิก หรือคัดค้าน ถอนความยินยอม หรือจำกัดการใช้หรือเปิดเผยข้อมูลส่วนบุคคล โปรดส่งคำขอไปที่',
    arcoIntroAfterEmail: '.',
    arcoRequirements: [
      'ระบุชื่อเต็มและช่องทางติดต่อ',
      'แนบสำเนาบัตรประจำตัวทางราชการที่ยังไม่หมดอายุ',
      'อธิบายสิทธิที่ต้องการใช้ให้ชัดเจน',
      'เพิ่มเอกสารที่ช่วยระบุตำแหน่งข้อมูลของคุณ หากมี',
    ],
    responseBeforeInai:
      'OLULO จะตอบกลับภายในสูงสุด 20 วันทำการตามมาตรา 32 ของ LFPDPPP หากไม่พอใจ คุณสามารถติดต่อ INAI ได้ที่',
    responseAfterInai: '.',
    inaiLinkLabel: 'www.inai.org.mx',
    documents: {
      privacyNotice: 'ประกาศความเป็นส่วนตัว',
      termsAndConditions: 'ข้อกำหนดและเงื่อนไข',
      cookiePolicy: 'นโยบายคุกกี้',
    },
  },
  nl: {
    backAriaLabel: 'Terug',
    pageTitle: 'Juridische informatie',
    companyName: 'OLULO, S.A. de C.V.',
    responsibleEyebrow: 'Verwerkingsverantwoordelijke',
    responsibleTitle: 'OLULO, S.A. de C.V.',
    legalNameLabel: 'Juridische naam',
    rfcLabel: 'RFC',
    taxAddressLabel: 'Fiscaal adres',
    privacyEmailLabel: 'Privacy-e-mail',
    publicDocumentsEyebrow: 'Openbare documenten',
    legalDocumentsTitle: 'Juridische documenten',
    documentUpdatedLabel: 'Laatst bijgewerkt',
    openPdf: 'PDF openen',
    arcoEyebrow: 'ARCO-rechten',
    arcoTitle: 'Kanaal om rechten uit te oefenen',
    arcoIntroBeforeEmail:
      'Om rechten op toegang, rectificatie, annulering of bezwaar uit te oefenen, toestemming in te trekken of het gebruik of de openbaarmaking van persoonsgegevens te beperken, stuurt u een verzoek naar',
    arcoIntroAfterEmail: '.',
    arcoRequirements: [
      'Vermeld uw volledige naam en een contactmethode.',
      'Voeg een kopie van een geldig officieel identiteitsbewijs toe.',
      'Beschrijf duidelijk welk recht u wilt uitoefenen.',
      'Voeg documenten toe die helpen uw gegevens te lokaliseren, indien van toepassing.',
    ],
    responseBeforeInai:
      'OLULO antwoordt binnen maximaal 20 werkdagen overeenkomstig artikel 32 van de LFPDPPP. Als u het niet eens bent, kunt u contact opnemen met INAI via',
    responseAfterInai: '.',
    inaiLinkLabel: 'www.inai.org.mx',
    documents: {
      privacyNotice: 'Privacyverklaring',
      termsAndConditions: 'Algemene voorwaarden',
      cookiePolicy: 'Cookiebeleid',
    },
  },
};

export function getLegalLandingPageTranslation(locale: Locale): LegalLandingPageTranslation {
  return legalLandingPageTranslations[locale] ?? legalLandingPageTranslations.es;
}
